import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Mic, Square, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function MeetingFormDialog({ open, onOpenChange, onCreated, defaultClientId }) {
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [clientId, setClientId] = useState(defaultClientId || "");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("10:00");
  const [meetingType, setMeetingType] = useState("In-Person");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    if (open) {
      if (!defaultClientId) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
      setTitle("");
      setNotes("");
      setClientId(defaultClientId || "");
      setDate(null);
      setTime("10:00");
      setMeetingType("In-Person");
      setSelectedEmployees([]);
      setIsRecording(false);
      setIsTranscribing(false);
    }
  }, [open, defaultClientId]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        setIsTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "meeting-notes.webm");
          const res = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const transcribed = (res.data.text || "").trim();
          if (transcribed) {
            setNotes((prev) => (prev ? `${prev} ${transcribed}` : transcribed));
          } else {
            toast.error("Didn't catch any speech. Please try again.");
          }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
          setIsTranscribing(false);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (e) {
      toast.error("Microphone access denied or unavailable.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const toggleEmployee = (email) => {
    setSelectedEmployees((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
  };

  const handleSubmit = async () => {
    if (!title || !clientId || !date) {
      toast.error("Title, client and meeting date are required");
      return;
    }
    setSubmitting(true);
    try {
      const [hh, mm] = time.split(":").map(Number);
      const meetingDateTime = new Date(date);
      meetingDateTime.setHours(hh || 0, mm || 0, 0, 0);

      let attendeeEmails = [...selectedEmployees];
      try {
        const clientRes = await api.get(`/clients/${clientId}`);
        if (clientRes.data.poc_email) attendeeEmails.push(clientRes.data.poc_email);
      } catch (e) {
        // proceed without client POC email if lookup fails
      }

      await api.post(`/clients/${clientId}/meetings`, {
        client_id: clientId,
        title,
        notes,
        meeting_date: meetingDateTime.toISOString(),
        meeting_type: meetingType,
        attendee_emails: attendeeEmails,
      });
      toast.success("Client meeting logged");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="meeting-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Log Client Meeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          {!defaultClientId && (
            <div className="space-y-1.5">
              <Label>Client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="meeting-form-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Meeting Title</Label>
            <Input data-testid="meeting-form-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Quarterly ESG Review" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Meeting Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="meeting-form-date-trigger" className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label>Meeting Time</Label>
              <Input data-testid="meeting-form-time-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Meeting Type</Label>
            <Select value={meetingType} onValueChange={setMeetingType}>
              <SelectTrigger data-testid="meeting-form-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="In-Person">In-Person</SelectItem>
                <SelectItem value="Call">Call</SelectItem>
                <SelectItem value="Video">Video</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Notes</Label>
              <button
                type="button"
                data-testid="meeting-notes-mic-button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                title={isRecording ? "Stop recording" : "Dictate notes"}
                className={cn(
                  "h-6 w-6 flex items-center justify-center rounded-full transition-colors duration-200",
                  isRecording ? "bg-red-600 text-white animate-pulse" : "bg-[#F7FEE7] text-[#00823B] hover:bg-[#76C121]/20"
                )}
              >
                {isTranscribing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isRecording ? <Square className="h-3 w-3" /> : <Mic className="h-3.5 w-3.5" />}
              </button>
            </div>
            <Textarea data-testid="meeting-form-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Agenda / discussion points, or click the mic to dictate" />
            {isRecording && <p data-testid="meeting-notes-recording-indicator" className="text-xs text-red-600">Recording... click the square to stop.</p>}
            {isTranscribing && <p data-testid="meeting-notes-transcribing-indicator" className="text-xs text-[#4B5563]">Transcribing...</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Invite Team Members (for Google Calendar invite)</Label>
            <div className="border border-[#E2E8F0] rounded-md p-2 space-y-1.5">
              {employees.length === 0 && <p className="text-xs text-[#4B5563] px-1">No employees found.</p>}
              {employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2 text-sm px-1 py-0.5 cursor-pointer" data-testid={`meeting-attendee-${emp.id}`}>
                  <Checkbox checked={selectedEmployees.includes(emp.email)} onCheckedChange={() => toggleEmployee(emp.email)} />
                  {emp.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="meeting-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="meeting-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : "Log Meeting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
